import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import type { MeResponse } from '@process-hub/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { api } from './api';
import { loginRequest, signOut } from './auth';
import { Shell } from './components/Shell';
import { config } from './config';
import { AuthRedirectError, describeError } from './errors';
import { Admin } from './screens/Admin';
import { Approvals } from './screens/Approvals';
import { Automation } from './screens/Automation';
import { Dashboard } from './screens/Dashboard';
import { Help } from './screens/Help';
import { Interview } from './screens/Interview';
import { InterviewStart } from './screens/InterviewStart';
import { Library } from './screens/Library';
import { ProcessDetail } from './screens/ProcessDetail';

/** Sign-in gate, then the app shell with the caller loaded from /api/me. */
export function App() {
  if (config.authDisabled) return <Authenticated />;
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      loadingComponent={() => <Centered>Signing you in with your Weaver account.</Centered>}
      errorComponent={({ error }) => <Centered error>Sign-in did not complete. {error?.errorMessage ?? ''}</Centered>}
    >
      <Authenticated />
    </MsalAuthenticationTemplate>
  );
}

function Authenticated() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<{ text: string; fatal: boolean } | null>(null);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      // An expired sign-in is not an error state: the redirect to Entra is already under way,
      // so say what is happening rather than showing a dead end. D-055.
      .catch((e: unknown) => setError({ text: describeError(e), fatal: !(e instanceof AuthRedirectError) }));
  }, []);

  if (error) return <Centered error={error.fatal}>{error.text}</Centered>;
  if (!me) return <Centered>Loading your departments.</Centered>;

  return (
    <Shell me={me} onSignOut={signOut}>
      <Routes>
        <Route path="/" element={<Library me={me} />} />
        <Route path="/processes/:slug" element={<ProcessDetail me={me} />} />
        <Route path="/dashboard" element={<Dashboard me={me} />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/interview" element={<InterviewStart me={me} />} />
        <Route path="/interview/:id" element={<Interview />} />
        <Route path="/admin" element={<Admin me={me} />} />
        <Route path="/automation" element={<Automation me={me} />} />
        <Route path="/help" element={<Help />} />
        <Route path="*" element={<div className="notice">That page does not exist.</div>} />
      </Routes>
    </Shell>
  );
}

function Centered({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100vh', background: 'var(--page-bg)' }}>
      <div className={`notice${error ? ' notice--error' : ''}`} style={{ maxWidth: 480, textAlign: 'center' }}>
        {children}
      </div>
    </div>
  );
}
